import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { OptionType } from "commons/ui/input/Select";

import { setValueByPath } from "commons/utils/form";
import { notEmptyValidator } from "commons/utils/validate";

import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useShowroomApi from "hooks/api/private/useShowroomApi";
import useShowroomImageApi from "hooks/api/private/useShowroomImageApi";
import { useShowroomId } from "hooks/useId";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";
import useOrderableImages, { OrderableImagesHook } from "hooks/useOrderableImages";

import { DesktopMobileForm, LanguageEnum } from "types/api/commons";
import { ShowroomBody, ShowroomDetails } from "types/api/showroom";
import { DesktopOrMobile } from "types/enums";

import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { getExtraErrorsDescription, notEmptyMedia } from "utils/validators";

export type ShowroomBodyForm = {
    group_id: string;
    enabled: boolean;
    graphical_order: number | null;
    virtual_tour: boolean;
    gallery: boolean;
    url_virtual_tour: string;
    cover_img: DesktopMobileForm;
    slug: string;
    text: {
        [x in LanguageEnum]: {
            title: string;
            address_1: string;
            address_2: string;
            virtual_tour_title_1: string;
            virtual_tour_title_2: string;
        };
    };
};

export type ShowroomFormHook = {
    continentGroup: OptionType[];
    formHook: FormHookType<ShowroomBodyForm>;
    orderableImagesHook: OrderableImagesHook;
    setDesktopMobileImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
};

function useShowroomForm(): ShowroomFormHook {
    const showroomId = useShowroomId();

    const { createShowroom, updateShowroom, getAllGroups, getOneShowroom } = useShowroomApi();

    const [schema, setSchema] = React.useState<ObjectPropertyType>({
        type: "object",
        properties: {
            group_id: {
                type: "simple",
                validators: [notEmptyValidator],
            },
            graphical_order: {
                type: "simple",
                validators: [notEmptyValidator],
            },
            url_virtual_tour: {
                type: "simple",
                validators: [],
            },
            slug: {
                type: "simple",
                validators: [notEmptyValidator],
            },
            cover_img: notEmptyMedia,
            text: {
                type: "collection",
                of: {
                    type: "object",
                    properties: {
                        title: {
                            type: "simple",
                            validators: [notEmptyValidator],
                        },
                        address_1: {
                            type: "simple",
                            validators: [notEmptyValidator],
                        },
                        address_2: {
                            type: "simple",
                            validators: [notEmptyValidator],
                        },
                        virtual_tour_title_1: {
                            type: "simple",
                            validators: [],
                        },
                        virtual_tour_title_2: {
                            type: "simple",
                            validators: [],
                        },
                    },
                },
            },
        },
    });

    const initialValues: ShowroomBodyForm = React.useMemo(() => {
        return {
            url_virtual_tour: "",
            group_id: "",
            enabled: true,
            virtual_tour: false,
            gallery: false,
            graphical_order: null,
            slug: "",
            cover_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            text: {
                it: {
                    title: "",
                    address_1: "",
                    address_2: "",
                    virtual_tour_title_1: "",
                    virtual_tour_title_2: "",
                },
                en: {
                    title: "",
                    address_1: "",
                    address_2: "",
                    virtual_tour_title_1: "",
                    virtual_tour_title_2: "",
                },
            },
        };
    }, []);

    const formHook = useForm<ShowroomBodyForm>(schema, initialValues, getExtraErrorsDescription);

    const { setForm, form, isValid, showAllError } = formHook;

    const { createShowroomImage, updateShowroomImage, deleteShowroomImage } = useShowroomImageApi();

    React.useEffect(() => {
        if (form.virtual_tour) {
            setSchema((prevSchema) => {
                const newSchema = _.cloneDeep(prevSchema);

                newSchema.properties.url_virtual_tour = {
                    type: "simple",
                    validators: [notEmptyValidator],
                };

                setValueByPath(newSchema, "properties.text.of.properties.virtual_tour_title_1", {
                    type: "simple",
                    validators: [notEmptyValidator],
                });
                setValueByPath(newSchema, "properties.text.of.properties.virtual_tour_title_2", {
                    type: "simple",
                    validators: [notEmptyValidator],
                });

                return newSchema;
            });
        } else {
            setSchema((prevSchema) => {
                const newSchema = _.cloneDeep(prevSchema);

                newSchema.properties.url_virtual_tour = {
                    type: "simple",
                    validators: [],
                };

                setValueByPath(newSchema, "properties.text.of.properties.virtual_tour_title_1", {
                    type: "simple",
                    validators: [],
                });
                setValueByPath(newSchema, "properties.text.of.properties.virtual_tour_title_2", {
                    type: "simple",
                    validators: [],
                });

                return newSchema;
            });
        }
    }, [form.virtual_tour]);

    const orderableImagesHook = useOrderableImages({
        createImage: createShowroomImage,
        updateImage: updateShowroomImage,
        deleteImage: deleteShowroomImage,
        fieldId: "showroom_id",
    });

    const {
        formHook: orderableImagesformHook,
        saveOrderableImages,
        initializeOrderableImage,
    } = orderableImagesHook;

    const {
        form: orderableImagesForm,
        isValid: isValidOrderableImages,
        showAllError: showAllErrorOrderableImages,
    } = orderableImagesformHook;

    const [continentGroup, setContinentGroup] = React.useState<OptionType[]>([]);

    React.useEffect(() => {
        const [apiCall, abortController] = getAllGroups();

        apiCall.then(setContinentGroup);

        return () => abortController.abort();
    }, [getAllGroups]);

    const { setDesktopMobileImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "cover_img",
    });

    const [showroomDetails, setShowroomDetails] = React.useState<ShowroomDetails | undefined>(
        undefined
    );

    React.useEffect(() => {
        if (showroomId) {
            const [apiCall, abortController] = getOneShowroom({ id: showroomId });

            apiCall.then(setShowroomDetails);

            return () => abortController.abort();
        }
    }, [getOneShowroom, setForm, showroomId]);

    // complete form from data
    React.useEffect(() => {
        if (showroomDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.group_id = showroomDetails.group_id;
                newForm.enabled = showroomDetails.enabled;
                newForm.graphical_order = showroomDetails.graphical_order;
                newForm.gallery = showroomDetails.gallery;
                newForm.virtual_tour = showroomDetails.virtual_tour;
                newForm.url_virtual_tour = showroomDetails.url_virtual_tour;
                newForm.slug = showroomDetails.slug;

                newForm.cover_img = {
                    id: showroomDetails.cover_img.id,
                    desktop: createAttachmentType({
                        id: showroomDetails.cover_img.desktop.id ?? null,
                        label: "Desktop",
                        filename: showroomDetails.cover_img.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: showroomDetails.cover_img.mobile.id ?? null,
                        label: "Mobile",
                        filename: showroomDetails.cover_img.mobile.filename,
                        status: 2,
                    }),
                };

                newForm.text.en.title = showroomDetails.text.en.title;
                newForm.text.en.address_1 = showroomDetails.text.en.address_1;
                newForm.text.en.address_2 = showroomDetails.text.en.address_2;
                newForm.text.en.virtual_tour_title_1 = showroomDetails.text.en.virtual_tour_title_1;
                newForm.text.en.virtual_tour_title_2 = showroomDetails.text.en.virtual_tour_title_2;

                newForm.text.it.title = showroomDetails.text.it.title;
                newForm.text.it.address_1 = showroomDetails.text.it.address_1;
                newForm.text.it.address_2 = showroomDetails.text.it.address_2;
                newForm.text.it.virtual_tour_title_1 = showroomDetails.text.it.virtual_tour_title_1;
                newForm.text.it.virtual_tour_title_2 = showroomDetails.text.it.virtual_tour_title_2;

                return newForm;
            });
        }
    }, [setForm, showroomDetails, showroomId]);

    // complete orderable images form from data
    React.useEffect(() => {
        if (showroomDetails) {
            initializeOrderableImage(showroomDetails.images);
        }
    }, [initializeOrderableImage, showroomDetails]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction("useShowroomForm", () => {
            if (isValid && (!form.gallery || isValidOrderableImages)) {
                const promises: Promise<unknown>[] = [];

                const body: ShowroomBody = {
                    group_id: form.group_id,
                    enabled: form.enabled,
                    graphical_order: form.graphical_order ?? 0,
                    gallery: form.gallery,
                    virtual_tour: form.virtual_tour,
                    url_virtual_tour: form.url_virtual_tour,
                    slug: form.slug,
                    cover_img: {
                        id: form.cover_img.id,
                        desktop: converterAttachmentToFileWithPayload(form.cover_img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.cover_img.mobile),
                    },
                    text: {
                        it: {
                            title: form.text.it.title,
                            address_1: form.text.it.address_1,
                            address_2: form.text.it.address_2,
                            virtual_tour_title_1: form.text.it.virtual_tour_title_1,
                            virtual_tour_title_2: form.text.it.virtual_tour_title_2,
                        },
                        en: {
                            title: form.text.en.title,
                            address_1: form.text.en.address_1,
                            address_2: form.text.en.address_2,
                            virtual_tour_title_1: form.text.en.virtual_tour_title_1,
                            virtual_tour_title_2: form.text.en.virtual_tour_title_2,
                        },
                    },
                };

                if (showroomId) {
                    const [apiCall] = updateShowroom({ id: showroomId }, body);

                    promises.push(apiCall);

                    if (form.gallery) {
                        promises.push(saveOrderableImages(showroomId));
                    }
                } else {
                    promises.push(
                        new Promise((resolve, reject) => {
                            const [apiCall] = createShowroom(body);

                            apiCall
                                .then((response) => {
                                    if (form.gallery) {
                                        saveOrderableImages(response.id)
                                            .then(resolve)
                                            .catch(reject);
                                    } else {
                                        resolve(response);
                                    }
                                })
                                .catch(reject);
                        })
                    );
                }

                return Promise.all(promises);
            } else {
                showAllError();
                showAllErrorOrderableImages();

                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveOrderableImages,
        createShowroom,
        updateShowroom,
        addSaveAction,
        showAllError,
        showAllErrorOrderableImages,
        showroomId,
        isValid,
        form,
        isValidOrderableImages,
        orderableImagesForm,
    ]);

    return React.useMemo(
        (): ShowroomFormHook => ({
            setDesktopMobileImage,
            orderableImagesHook,
            continentGroup,
            formHook,
        }),
        [setDesktopMobileImage, orderableImagesHook, continentGroup, formHook]
    );
}

export default useShowroomForm;
